/**
 * Authenticated fetch wrapper - adds Bearer token and handles 401 refresh retry
 *
 * Use this instead of raw fetch for API calls that require authentication.
 * On 401, attempts token refresh and retries the request once.
 *
 * When running as a Module Federation remote (peri-tray, peri-track), the remote
 * may have its own bundled copy of this module with an empty tokenStore.
 * The host (periverse) registers a global token provider so remotes can obtain
 * the token from the host's tokenStore.
 */

import { tokenStore } from './token-store';
import { getGaClientId, getTenantIdHash } from '@peritasai/analytics';

// Generated once at module load — stable for the tab session.
// Falls back to timestamp+random on non-HTTPS hosts (crypto.randomUUID requires a secure context).
const SESSION_REQUEST_ID =
  typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

declare global {
  interface Window {
    __PERIVERSE_TOKEN_PROVIDER__?: () => Promise<string | null>;
    __PERIVERSE_HAS_TOKENS__?: () => boolean;
    __PERIVERSE_REFRESH_TOKEN__?: () => Promise<string | null>;
  }
}

/**
 * Get access token - uses global provider from host when available (Module Federation remotes)
 */
async function getAccessTokenForRequest(): Promise<string | null> {
  if (
    typeof window !== 'undefined' &&
    typeof window.__PERIVERSE_TOKEN_PROVIDER__ === 'function'
  ) {
    return window.__PERIVERSE_TOKEN_PROVIDER__();
  }
  return tokenStore.getAccessTokenOrRefresh();
}

function hasTokensForRequest(): boolean {
  if (
    typeof window !== 'undefined' &&
    typeof window.__PERIVERSE_HAS_TOKENS__ === 'function'
  ) {
    return window.__PERIVERSE_HAS_TOKENS__();
  }
  return tokenStore.hasTokens();
}

async function refreshTokenForRequest(): Promise<string | null> {
  if (
    typeof window !== 'undefined' &&
    typeof window.__PERIVERSE_REFRESH_TOKEN__ === 'function'
  ) {
    return window.__PERIVERSE_REFRESH_TOKEN__();
  }
  return tokenStore.refresh();
}

/**
 * Register global token provider (called by host periverse).
 * Enables Module Federation remotes (peri-tray, peri-track) to obtain the token
 * when they have their own bundled copy of @peritasai/data-access.
 */
export function registerGlobalTokenProvider(): void {
  if (typeof window === 'undefined') return;
  window.__PERIVERSE_TOKEN_PROVIDER__ = () =>
    tokenStore.getAccessTokenOrRefresh();
  window.__PERIVERSE_HAS_TOKENS__ = () => tokenStore.hasTokens();
  window.__PERIVERSE_REFRESH_TOKEN__ = () => tokenStore.refresh();
}

/**
 * Unregister global token provider (called on logout)
 */
export function unregisterGlobalTokenProvider(): void {
  if (typeof window === 'undefined') return;
  delete window.__PERIVERSE_TOKEN_PROVIDER__;
  delete window.__PERIVERSE_HAS_TOKENS__;
  delete window.__PERIVERSE_REFRESH_TOKEN__;
}

/**
 * Fetch with Bearer token. On 401, refreshes token and retries once.
 * Uses credentials: 'omit' - JWT Bearer token is the sole auth mechanism, no session cookies.
 */
export async function authenticatedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const accessToken = await getAccessTokenForRequest();
  const headers = new Headers(init?.headers);

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const gaClientId = getGaClientId();
  if (gaClientId) {
    headers.set('x-analytics-pseudo-id', gaClientId);
  }

  const tenantIdHash = getTenantIdHash();
  if (tenantIdHash) {
    headers.set('x-tenant-id', tenantIdHash);
  }

  headers.set('x-request-id', SESSION_REQUEST_ID);

  const credentials = init?.credentials ?? 'omit';
  let response = await fetch(input, {
    ...init,
    headers,
    credentials,
  });

  // On 401, try refresh and retry once
  if (response.status === 401 && hasTokensForRequest()) {
    const newToken = await refreshTokenForRequest();
    if (newToken) {
      headers.set('Authorization', `Bearer ${newToken}`);
      response = await fetch(input, {
        ...init,
        headers,
        credentials,
      });
    }
  }

  return response;
}

/**
 * Get Authorization header value for manual fetch/SSE setup
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const accessToken = await getAccessTokenForRequest();
  if (accessToken) {
    return { Authorization: `Bearer ${accessToken}` };
  }
  return {};
}
